import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { Button, Input, Select } from '../ui/Kit';
import Spinner from '../ui/Spinner';
import { api } from '../../lib/api';

const ACTIVITY_TYPES = [
  { value: 'presentation', label: 'Presentation' },
  { value: 'app_submitted', label: 'App Submitted' },
  { value: 'referral', label: 'Referral Gathered' },
  { value: 'objection', label: 'Objection Received' },
  { value: 'manual_calls', label: 'Manual Calls' },
];

const CALL_OUTCOMES = [
  { value: 'answered', label: 'Answered' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_answer', label: 'No Answer' },
];

function todayDateInput() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function emptyForm(type = 'presentation') {
  return {
    activity_type: type,
    activity_date: todayDateInput(),
    amount_dollars: '',
    policy_type: '',
    referral_name: '',
    referral_phone: '',
    objection_text: '',
    how_handled: '',
    call_count: '1',
    call_outcome: 'answered',
    notes: '',
  };
}

export default function LogActivityModal({ isOpen, onClose, leadId, onSuccess }) {
  const [form, setForm] = useState(() => emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm());
      setError('');
    }
  }, [isOpen]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const type = form.activity_type;

    if (type === 'app_submitted') {
      const amt = parseFloat(form.amount_dollars);
      if (Number.isNaN(amt) || amt < 0) {
        setError('Enter a valid $AP amount.');
        return;
      }
      if (!form.policy_type.trim()) {
        setError('Policy type is required.');
        return;
      }
    }
    if (type === 'referral' && !form.referral_name.trim()) {
      setError('Referral name is required.');
      return;
    }
    if (type === 'objection') {
      if (!form.objection_text.trim()) {
        setError('Objection text is required.');
        return;
      }
      if (!form.how_handled.trim()) {
        setError('How handled is required.');
        return;
      }
    }
    if (type === 'manual_calls') {
      const count = parseInt(form.call_count, 10);
      if (!count || count < 1) {
        setError('Enter at least 1 call.');
        return;
      }
    }

    setSaving(true);
    try {
      await api.activityLogs.create(leadId, {
        activity_type: type,
        activity_date: form.activity_date,
        amount_dollars:
          type === 'app_submitted' ? parseFloat(form.amount_dollars) : null,
        policy_type: type === 'app_submitted' ? form.policy_type.trim() : null,
        referral_name: type === 'referral' ? form.referral_name.trim() : null,
        referral_phone:
          type === 'referral' && form.referral_phone.trim()
            ? form.referral_phone.trim()
            : null,
        objection_text: type === 'objection' ? form.objection_text.trim() : null,
        how_handled: type === 'objection' ? form.how_handled.trim() : null,
        call_count: type === 'manual_calls' ? parseInt(form.call_count, 10) : null,
        call_outcome: type === 'manual_calls' ? form.call_outcome : null,
        notes: form.notes.trim() || null,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save activity.');
    } finally {
      setSaving(false);
    }
  };

  const type = form.activity_type;

  return (
    <Modal isOpen={isOpen} onClose={() => !saving && onClose()} title="Log Activity" className="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Activity type"
          value={form.activity_type}
          onChange={(e) => setForm(emptyForm(e.target.value))}
          options={ACTIVITY_TYPES}
          disabled={saving}
        />

        {type === 'presentation' && (
          <>
            <Input
              label="Date"
              type="date"
              value={form.activity_date}
              onChange={set('activity_date')}
              disabled={saving}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                className="w-full border border-gray-200 rounded-md p-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[72px] resize-y"
                value={form.notes}
                onChange={set('notes')}
                disabled={saving}
                placeholder="Optional notes…"
              />
            </div>
          </>
        )}

        {type === 'app_submitted' && (
          <>
            <Input
              label="$AP"
              type="number"
              min="0"
              step="0.01"
              value={form.amount_dollars}
              onChange={set('amount_dollars')}
              disabled={saving}
              placeholder="0.00"
            />
            <Input
              label="Policy type"
              value={form.policy_type}
              onChange={set('policy_type')}
              disabled={saving}
              placeholder="e.g. Term, Whole Life"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                className="w-full border border-gray-200 rounded-md p-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[72px] resize-y"
                value={form.notes}
                onChange={set('notes')}
                disabled={saving}
              />
            </div>
          </>
        )}

        {type === 'referral' && (
          <>
            <Input
              label="Referral name"
              value={form.referral_name}
              onChange={set('referral_name')}
              disabled={saving}
            />
            <Input
              label="Phone (optional)"
              value={form.referral_phone}
              onChange={set('referral_phone')}
              disabled={saving}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                className="w-full border border-gray-200 rounded-md p-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[72px] resize-y"
                value={form.notes}
                onChange={set('notes')}
                disabled={saving}
              />
            </div>
          </>
        )}

        {type === 'objection' && (
          <>
            <Input
              label="Objection"
              value={form.objection_text}
              onChange={set('objection_text')}
              disabled={saving}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">How handled</label>
              <textarea
                className="w-full border border-gray-200 rounded-md p-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[88px] resize-y"
                value={form.how_handled}
                onChange={set('how_handled')}
                disabled={saving}
              />
            </div>
          </>
        )}

        {type === 'manual_calls' && (
          <>
            <Input
              label="Number of calls"
              type="number"
              min="1"
              step="1"
              value={form.call_count}
              onChange={set('call_count')}
              disabled={saving}
            />
            <Select
              label="Outcome"
              value={form.call_outcome}
              onChange={set('call_outcome')}
              options={CALL_OUTCOMES}
              disabled={saving}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                className="w-full border border-gray-200 rounded-md p-2.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[72px] resize-y"
                value={form.notes}
                onChange={set('notes')}
                disabled={saving}
              />
            </div>
          </>
        )}

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={saving}>
            {saving ? (
              <>
                <Spinner className="mr-2" />
                Saving…
              </>
            ) : (
              'Save Activity'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
